import React from "react";
import {
  Modal,
  Tabs,
  Tag,
  Empty,
  Table,
  Tooltip,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  FileTextOutlined,
  TruckOutlined,
  ToolOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  MinusCircleOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import type { SummaryRowResponse, ServiceSummary } from "@OrianaTypes";
import moment from "moment";
import {
  formatLabel,
  getPoStatusColor,
  getPaymentStatusColor,
} from "../utils";
import "./SummaryDetailModal.css";

interface SummaryDetailModalProps {
  visible: boolean;
  onClose: () => void;
  po: SummaryRowResponse | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (d?: string | null) => (d ? moment(d).format("DD-MM-YYYY") : "-");
const fmtCurrency = (n?: number | null) =>
  n != null ? `₹${Number(n).toLocaleString("en-IN")}` : "-";

const fmtPct = (val?: number) => {
  if (val === undefined || val === null) return "-";
  const color = val >= 100 ? "#16a34a" : val > 0 ? "#d97706" : "#dc2626";
  return (
    <span style={{ color, fontWeight: 600 }}>
      {val.toFixed(1)}%
    </span>
  );
};

const getAccordionTagColor = (status: string) => {
  if (status === "Done") return "success";
  if (status === "In-Progress") return "warning";
  return "default";
};

const getAccordionIcon = (status: string) => {
  if (status === "Done")
    return <CheckCircleOutlined className="sdm-pipeline-icon sdm-pipeline-icon--done" />;
  if (status === "In-Progress")
    return <SyncOutlined spin className="sdm-pipeline-icon sdm-pipeline-icon--progress" />;
  return <MinusCircleOutlined className="sdm-pipeline-icon sdm-pipeline-icon--none" />;
};

const statusTag = (status?: string | null) =>
  status ? <Tag color={getAccordionTagColor(status)}>{formatLabel(status)}</Tag> : <span className="sdm-dash">—</span>;

// ─── Component ────────────────────────────────────────────────────────────────

const SummaryDetailModal: React.FC<SummaryDetailModalProps> = ({
  visible,
  onClose,
  po,
}) => {
  if (!po) return null;

  // ── Tab 1: Overview ──────────────────────────────────────────────────────

  const pipeline = [
    {
      key: "dispatch",
      label: "Dispatch",
      acc: po.accordionStatus?.dispatch,
      countLabel: (acc: typeof po.accordionStatus.dispatch) =>
        `${acc.dispatchedQty}/${acc.totalQty} qty`,
    },
    {
      key: "delivery",
      label: "Delivery",
      acc: po.accordionStatus?.delivery,
      countLabel: (acc: { completed: number; total: number }) =>
        `${acc.completed}/${acc.total}`,
    },
    {
      key: "preCommissioning",
      label: "Pre-Comm",
      acc: po.accordionStatus?.preCommissioning,
      countLabel: (acc: { completed: number; total: number }) =>
        `${acc.completed}/${acc.total}`,
    },
    {
      key: "commissioning",
      label: "Commissioning",
      acc: po.accordionStatus?.commissioning,
      countLabel: (acc: { completed: number; total: number }) =>
        `${acc.completed}/${acc.total}`,
    },
    {
      key: "warranty",
      label: "Warranty",
      acc: po.accordionStatus?.warranty,
      countLabel: (acc: { completed: number; total: number }) =>
        `${acc.completed}/${acc.total}`,
    },
  ] as const;

  const renderOverview = () => (
    <div className="sdm-overview">
      {/* PO Header strip */}
      <div className="sdm-header-strip">
        <div className="sdm-header-left">
          <Tag color="blue" className="sdm-po-id-tag">{po.poId}</Tag>
          <Tag color={getPoStatusColor(po.poStatus)}>{formatLabel(po.poStatus)}</Tag>
          <Tag color={getPaymentStatusColor(po.paymentStatus)}>{formatLabel(po.paymentStatus)}</Tag>
        </div>
        <div className="sdm-header-right">
          <span className="sdm-header-client">{po.clientName || "—"}</span>
        </div>
      </div>

      {/* PO Meta grid */}
      <div className="sdm-meta-grid">
        <div className="sdm-meta-item">
          <span className="sdm-meta-label">OSG PI No</span>
          <span className="sdm-meta-value">{po.osgPiNo || "—"}</span>
        </div>
        <div className="sdm-meta-item">
          <span className="sdm-meta-label">OSG PI Date</span>
          <span className="sdm-meta-value">{fmtDate(po.osgPiDate)}</span>
        </div>
        <div className="sdm-meta-item">
          <span className="sdm-meta-label">Client PO No</span>
          <span className="sdm-meta-value">{po.clientPoNo || "—"}</span>
        </div>
        <div className="sdm-meta-item">
          <span className="sdm-meta-label">Client PO Date</span>
          <span className="sdm-meta-value">{fmtDate(po.clientPoDate)}</span>
        </div>
        <div className="sdm-meta-item">
          <span className="sdm-meta-label">Sales Person</span>
          <span className="sdm-meta-value">{po.createdByName || "—"}</span>
        </div>
        <div className="sdm-meta-item">
          <span className="sdm-meta-label">Site Location</span>
          <span className="sdm-meta-value">{po.siteLocation || "—"}</span>
        </div>
        <div className="sdm-meta-item">
          <span className="sdm-meta-label">Assigned (Dispatch)</span>
          <span className="sdm-meta-value">{po.assignedUserName || "—"}</span>
        </div>
        <div className="sdm-meta-item">
          <span className="sdm-meta-label">Assigned (Service)</span>
          <span className="sdm-meta-value">{po.assignedServiceUserName || "—"}</span>
        </div>
        <div className="sdm-meta-item">
          <span className="sdm-meta-label">Category</span>
          <span className="sdm-meta-value">{po.categoryNames?.join(", ") || "—"}</span>
        </div>
        <div className="sdm-meta-item">
          <span className="sdm-meta-label">OEM</span>
          <span className="sdm-meta-value">{po.oemNames?.join(", ") || "—"}</span>
        </div>
        <div className="sdm-meta-item">
          <span className="sdm-meta-label">Product</span>
          <span className="sdm-meta-value">{po.productNames?.join(", ") || "—"}</span>
        </div>
        <div className="sdm-meta-item">
          <span className="sdm-meta-label">Total Quantity</span>
          <span className="sdm-meta-value">{po.totalQuantity?.toLocaleString("en-IN") ?? "—"}</span>
        </div>
        {po.remarks && (
          <div className="sdm-meta-item sdm-meta-item--full">
            <span className="sdm-meta-label">Remarks</span>
            <span className="sdm-meta-value">{po.remarks}</span>
          </div>
        )}
      </div>

      {/* Pipeline progress tracker */}
      <div className="sdm-section-title">
        <SyncOutlined className="sdm-section-icon" /> Pipeline Progress
      </div>
      <div className="sdm-pipeline">
        {pipeline.map((step, idx) => {
          const acc = step.acc as { status: string; completed?: number; total?: number; dispatchedQty?: number; totalQty?: number } | undefined;
          const status = acc?.status ?? "Not Started";
          const count =
            step.key === "dispatch"
              ? `${(acc as { dispatchedQty?: number })?.dispatchedQty ?? 0}/${(acc as { totalQty?: number })?.totalQty ?? 0} qty`
              : `${(acc as { completed?: number })?.completed ?? 0}/${(acc as { total?: number })?.total ?? 0}`;
          return (
            <React.Fragment key={step.key}>
              <div className={`sdm-pipeline-step sdm-pipeline-step--${status === "Done" ? "done" : status === "In-Progress" ? "progress" : "none"}`}>
                <div className="sdm-pipeline-icon-wrap">
                  {getAccordionIcon(status)}
                </div>
                <div className="sdm-pipeline-label">{step.label}</div>
                <Tag
                  color={getAccordionTagColor(status)}
                  className="sdm-pipeline-status-tag"
                >
                  {status}
                </Tag>
                <div className="sdm-pipeline-count">{count}</div>
              </div>
              {idx < pipeline.length - 1 && <div className="sdm-pipeline-connector" />}
            </React.Fragment>
          );
        })}
      </div>

      {/* Financial summary */}
      <div className="sdm-section-title">
        <DollarOutlined className="sdm-section-icon" /> Financial Summary
      </div>
      <div className="sdm-finance-strip">
        <div className="sdm-finance-card sdm-finance-card--total">
          <div className="sdm-finance-label">Total PO Cost</div>
          <div className="sdm-finance-value">{fmtCurrency(po.totalPrice)}</div>
        </div>
        <div className="sdm-finance-card sdm-finance-card--received">
          <div className="sdm-finance-label">Amount Received</div>
          <div className="sdm-finance-value">{fmtCurrency(po.amountReceived)}</div>
        </div>
        <div className="sdm-finance-card sdm-finance-card--balance">
          <div className="sdm-finance-label">Balance</div>
          <div className="sdm-finance-value">{fmtCurrency(po.remainingAmount)}</div>
        </div>
        <div className="sdm-finance-card sdm-finance-card--dispatched">
          <div className="sdm-finance-label">Dispatched Qty</div>
          <div className="sdm-finance-value">{po.dispatchedQuantity?.toLocaleString("en-IN") ?? "0"}</div>
          <div className="sdm-finance-sub">{fmtPct(po.dispatchPercentage)}</div>
        </div>
      </div>
    </div>
  );

  // ── Tab 2: Dispatches & Delivery ─────────────────────────────────────────

  const renderDispatches = () => {
    if (!po.dispatches?.length) {
      return <Empty description="No dispatches available" className="sdm-empty" />;
    }

    return (
      <div className="sdm-dispatch-list">
        {po.dispatches.map((dispatch) => {
          const serialCount = po.services?.filter(
            (s) => s.dispatchId === dispatch.dispatchId
          ).length ?? 0;
          return (
            <div key={dispatch.dispatchId} className="sdm-dispatch-card">
              <div className="sdm-dispatch-header">
                <div className="sdm-dispatch-header-left">
                  <TruckOutlined className="sdm-dispatch-icon" />
                  <span className="sdm-dispatch-title">Dispatch #{dispatch.dispatchId}</span>
                  {dispatch.dispatchStatus && (
                    <Tag>{formatLabel(dispatch.dispatchStatus)}</Tag>
                  )}
                </div>
                <div className="sdm-dispatch-header-right">
                  {dispatch.deliveryStatus ? (
                    <Tag color="success">{formatLabel(dispatch.deliveryStatus)}</Tag>
                  ) : (
                    <Tag color="default">Not Delivered</Tag>
                  )}
                  {serialCount > 0 && (
                    <Tooltip title={`${serialCount} serial(s) in this dispatch`}>
                      <span className="sdm-serial-count">{serialCount} serial{serialCount !== 1 ? "s" : ""}</span>
                    </Tooltip>
                  )}
                </div>
              </div>
              <div className="sdm-dispatch-body">
                <div className="sdm-dispatch-row">
                  <div className="sdm-dispatch-field">
                    <span className="sdm-field-label">Tax Invoice</span>
                    <span className="sdm-field-value">{dispatch.taxInvoiceNumber || "—"}</span>
                  </div>
                  <div className="sdm-dispatch-field">
                    <span className="sdm-field-label">Invoice Date</span>
                    <span className="sdm-field-value">{fmtDate(dispatch.invoiceDate)}</span>
                  </div>
                  <div className="sdm-dispatch-field">
                    <span className="sdm-field-label">Dispatch Date</span>
                    <span className="sdm-field-value">{fmtDate(dispatch.dispatchDate)}</span>
                  </div>
                  <div className="sdm-dispatch-field">
                    <span className="sdm-field-label">Dispatched Qty</span>
                    <span className="sdm-field-value">{dispatch.dispatchedQuantity.toLocaleString("en-IN")}</span>
                  </div>
                </div>
                <div className="sdm-dispatch-row">
                  <div className="sdm-dispatch-field">
                    <span className="sdm-field-label">Delivery Location</span>
                    <span className="sdm-field-value">{dispatch.deliveryLocation || "—"}</span>
                  </div>
                  <div className="sdm-dispatch-field">
                    <span className="sdm-field-label">Delivery Pincode</span>
                    <span className="sdm-field-value">{dispatch.deliveryPincode || "—"}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Summary strip */}
        <div className="sdm-dispatch-summary">
          <div className="sdm-dispatch-summary-item">
            <span className="sdm-dispatch-summary-label">Total Dispatched</span>
            <span className="sdm-dispatch-summary-value">
              {po.dispatchedQuantity?.toLocaleString("en-IN") ?? "0"}
            </span>
          </div>
          <div className="sdm-dispatch-summary-item">
            <span className="sdm-dispatch-summary-label">Delivered</span>
            <span className="sdm-dispatch-summary-value">
              {po.deliveredQuantity?.toLocaleString("en-IN") ?? "0"}
            </span>
          </div>
          <div className="sdm-dispatch-summary-item">
            <span className="sdm-dispatch-summary-label">Balance</span>
            <span className="sdm-dispatch-summary-value">
              {po.balanceQuantity?.toLocaleString("en-IN") ?? "—"}
            </span>
          </div>
          <div className="sdm-dispatch-summary-item">
            <span className="sdm-dispatch-summary-label">Dispatch %</span>
            <span className="sdm-dispatch-summary-value">{fmtPct(po.dispatchPercentage)}</span>
          </div>
          <div className="sdm-dispatch-summary-item">
            <span className="sdm-dispatch-summary-label">Delivery %</span>
            <span className="sdm-dispatch-summary-value">{fmtPct(po.deliveryPercentage)}</span>
          </div>
        </div>
      </div>
    );
  };

  // ── Tab 3: Services by Dispatch ──────────────────────────────────────────

  const serviceTableColumns: ColumnsType<ServiceSummary> = [
    {
      title: "Serial No",
      dataIndex: "serialNumber",
      key: "serialNumber",
      width: 120,
      render: (v: string) => <span className="sdm-serial">{v || "—"}</span>,
    },
    {
      title: "Product",
      dataIndex: "productName",
      key: "productName",
      width: 130,
      ellipsis: true,
      render: (v: string) => v || "—",
    },
    {
      title: "PPM",
      dataIndex: "ppmConfirmationStatus",
      key: "ppmConfirmationStatus",
      width: 90,
      render: (v: string) => statusTag(v),
    },
    {
      title: "Pre-Comm",
      dataIndex: "preCommissioningStatus",
      key: "preCommissioningStatus",
      width: 100,
      render: (v: string) => statusTag(v),
    },
    {
      title: "ECD from Client",
      dataIndex: "commissioningEcdFromClient",
      key: "commissioningEcdFromClient",
      width: 130,
      render: (v: string) => fmtDate(v),
    },
    {
      title: "Assigned Engineer",
      dataIndex: "assignedServiceEngineer",
      key: "assignedServiceEngineer",
      width: 140,
      ellipsis: true,
      render: (v: string) => v || "—",
    },
    {
      title: "Comm. Date",
      dataIndex: "commissioningDate",
      key: "commissioningDate",
      width: 110,
      render: (v: string) => fmtDate(v),
    },
    {
      title: "Commissioning",
      dataIndex: "commissioningStatus",
      key: "commissioningStatus",
      width: 110,
      render: (v: string) => statusTag(v),
    },
    {
      title: "Warranty",
      dataIndex: "warrantyStatus",
      key: "warrantyStatus",
      width: 100,
      render: (v: string, record: ServiceSummary) => (
        <div>
          {statusTag(v)}
          {record.warrantyCertificateNo && (
            <div className="sdm-cert-no">
              <Tooltip title="Warranty Certificate No">
                {record.warrantyCertificateNo}
              </Tooltip>
            </div>
          )}
        </div>
      ),
    },
  ];

  const renderServices = () => {
    if (!po.services?.length) {
      return <Empty description="No services available" className="sdm-empty" />;
    }

    const dispatchGroups = (po.dispatches ?? []).map((d) => ({
      dispatch: d,
      services: po.services!.filter((s) => s.dispatchId === d.dispatchId),
    }));

    // Services not linked to any dispatch
    const linkedIds = new Set(po.dispatches?.map((d) => d.dispatchId) ?? []);
    const unlinked = po.services.filter((s) => !linkedIds.has(s.dispatchId));

    const commissioningBalance =
      po.dispatchedQuantity != null && po.commissioningQuantity != null
        ? Math.max(0, po.dispatchedQuantity - po.commissioningQuantity)
        : null;
    const warrantyBalance =
      po.commissioningQuantity != null && po.warrantyQuantity != null
        ? Math.max(0, po.commissioningQuantity - po.warrantyQuantity)
        : null;

    return (
      <div className="sdm-services-list">
        {dispatchGroups.map(({ dispatch, services }) => (
          <div key={dispatch.dispatchId} className="sdm-service-group">
            <div className="sdm-service-group-header">
              <TruckOutlined className="sdm-service-group-icon" />
              <span className="sdm-service-group-title">
                Dispatch #{dispatch.dispatchId}
              </span>
              {dispatch.deliveryStatus ? (
                <Tag color="success">{formatLabel(dispatch.deliveryStatus)}</Tag>
              ) : (
                <Tag color="default">Not Delivered</Tag>
              )}
              <span className="sdm-service-group-count">
                {services.length} serial{services.length !== 1 ? "s" : ""}
              </span>
            </div>
            {services.length === 0 ? (
              <div className="sdm-service-group-empty">No serials in this dispatch</div>
            ) : (
              <Table<ServiceSummary>
                dataSource={services.map((s) => ({ ...s, key: s.serviceId }))}
                columns={serviceTableColumns}
                size="small"
                pagination={false}
                scroll={{ x: "max-content" }}
                className="sdm-service-table"
              />
            )}
          </div>
        ))}

        {unlinked.length > 0 && (
          <div className="sdm-service-group">
            <div className="sdm-service-group-header">
              <ToolOutlined className="sdm-service-group-icon" />
              <span className="sdm-service-group-title">Other Services</span>
            </div>
            <Table<ServiceSummary>
              dataSource={unlinked.map((s) => ({ ...s, key: s.serviceId }))}
              columns={serviceTableColumns}
              size="small"
              pagination={false}
              scroll={{ x: "max-content" }}
              className="sdm-service-table"
            />
          </div>
        )}

        {/* Summary strip */}
        <div className="sdm-dispatch-summary">
          <div className="sdm-dispatch-summary-item">
            <span className="sdm-dispatch-summary-label">Commissioning Qty</span>
            <span className="sdm-dispatch-summary-value">
              {po.commissioningQuantity?.toLocaleString("en-IN") ?? "0"}
            </span>
          </div>
          <div className="sdm-dispatch-summary-item">
            <span className="sdm-dispatch-summary-label">Comm. Balance</span>
            <span className="sdm-dispatch-summary-value">
              {commissioningBalance != null
                ? commissioningBalance.toLocaleString("en-IN")
                : "—"}
            </span>
          </div>
          <div className="sdm-dispatch-summary-item">
            <span className="sdm-dispatch-summary-label">Commissioning %</span>
            <span className="sdm-dispatch-summary-value">{fmtPct(po.commissioningPercentage)}</span>
          </div>
          <div className="sdm-dispatch-summary-item">
            <span className="sdm-dispatch-summary-label">Warranty Qty</span>
            <span className="sdm-dispatch-summary-value">
              {po.warrantyQuantity?.toLocaleString("en-IN") ?? "0"}
            </span>
          </div>
          <div className="sdm-dispatch-summary-item">
            <span className="sdm-dispatch-summary-label">Warranty Balance</span>
            <span className="sdm-dispatch-summary-value">
              {warrantyBalance != null
                ? warrantyBalance.toLocaleString("en-IN")
                : "—"}
            </span>
          </div>
          <div className="sdm-dispatch-summary-item">
            <span className="sdm-dispatch-summary-label">Warranty %</span>
            <span className="sdm-dispatch-summary-value">{fmtPct(po.warrantyPercentage)}</span>
          </div>
        </div>
      </div>
    );
  };

  // ── Tabs ─────────────────────────────────────────────────────────────────

  const tabItems = [
    {
      key: "overview",
      label: (
        <span>
          <FileTextOutlined /> Overview
        </span>
      ),
      children: renderOverview(),
    },
    {
      key: "dispatches",
      label: (
        <span>
          <TruckOutlined /> Dispatches &amp; Delivery
          <span className="sdm-tab-count">{po.dispatches?.length ?? 0}</span>
        </span>
      ),
      children: renderDispatches(),
    },
    {
      key: "services",
      label: (
        <span>
          <ToolOutlined /> Services
          <span className="sdm-tab-count">{po.services?.length ?? 0}</span>
        </span>
      ),
      children: renderServices(),
    },
  ];

  return (
    <Modal
      title={
        <span className="sdm-modal-title">
          Order Details — <span className="sdm-modal-po-id">{po.poId}</span>
        </span>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1100}
      style={{ top: 20 }}
      className="sdm-modal"
    >
      <Tabs defaultActiveKey="overview" items={tabItems} className="sdm-tabs" />
    </Modal>
  );
};

export default SummaryDetailModal;
